/**
=========================================================
* E4A-IoT-Manager React Frontend Dashboard 
* Copyright © 2024 Electronics4All Inc.
=========================================================
*/

import { useEffect, useState, useMemo } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { InputLabel, Autocomplete } from "@mui/material";

// Material Dashboard React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDAvatar from "components/MDAvatar";
import MDDropzone from "components/MDDropzone";

// E4A React components
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";
import Footer from "e4acomponents/Footer";
import FormField from "layouts/applications/wizard/components/FormField";
import { useNavigate } from "react-router-dom";

import CrudService from "services/cruds-service";
import organizationsService from "services/organizations.service";
import { useAbility } from "@casl/react";
import { AbilityContext } from "Can";

const NewUser = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [image, setImage] = useState("");
  const [fileState, setFileState] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [user, setUser] = useState({
    name: "",
    email: "",
    password: "",
    confirm: "",
    role: "",
    organization: "",
  });

  const [value, setValue] = useState("");
  const [selOrg, setSelOrg] = useState("");

  const [newImage, setNewImage] = useState("");

  const [error, setError] = useState({
    name: false,
    email: false,
    password: false,
    confirm: false,
    role: false,
    error: false,
    organization: false,
    textError: "",
  });

  const ability = useAbility(AbilityContext);

  useEffect(()=> {console.log("Selected Org", selOrg)}, [selOrg]);

  useEffect(() => {
    (async () => {
      try {
        const response = await CrudService.getRoles();
        setRoles(response.data);


        if (ability.can("create", "organizations")) {
          const orgsResponse = await organizationsService.getOrganizations();

          const mapped = orgsResponse.map((m) => ({
            label: m.name,
            id: m._id,
          }));
          setOrganizations(mapped);
        }
      } catch (err) {
        console.error(err);
        return null;
      }
    })();
  }, []);

  const changeHandler = (e) => {
    setUser({
      ...user,
      [e.target.name]: e.target.value,
    });
  };

  // Whenever an image is added to the dropzone, it is stored in newImage
  const onImageAdd = (file) => {
    console.log(file);
    setNewImage(file);
  };

  const errorChangeHandler = (key, text) => {
    setError({
      ...error,
      [key]: false,
      textError: text
    });
  }
  const submitHandler = async (e) => {
    e.preventDefault();

    const mailFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (user.name.trim().length === 0) {
      errorChangeHandler("name", "The name cannot be empty");
      return;
    }

    if (user.email.trim().length === 0 || !user.email.trim().match(mailFormat)) {
      errorChangeHandler("email", "The email is not valid");
      return;
    }

    if (user.password.trim().length < 8) {
      errorChangeHandler("password", "The password should have at least 8 characters");
      return;
    }

    if (user.confirm.trim() !== user.password.trim()) {
      errorChangeHandler("confirm", "The password and password confirmation do not match");
      return;
    }

    if (value.id === "") {
      errorChangeHandler("role", "Role is required");
      return;
    }

    const payload = {
      filename: newImage.upload.filename,
      dataUrl: newImage.dataURL,
      folderpath: user.name
    }
    const imagePath = await CrudService.setUserImage(payload);
    const newUser = {
      data: {
        type: "users",
        attributes: {
          name: user.name,
          email: user.email,
          password: user.password,
          password_confirmation: user.confirm,
          profile_image: imagePath.s3Key,
          organization: selOrg ? selOrg.id : null,
        },
        relationships: {
          roles: {
            data: [
              {
                id: value.id.toString(),
                type: "roles",
              },
            ],
          },
        },
      },
    };

    let res = null;
    try {
      res = await CrudService.createUser(newUser);
    } catch (err) {
      setError({ ...error, error: true, textError: err.message });
      return null;
    }
    navigate("/examples-api/user-management", {
      state: { value: true, text: "User created successfully" },
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={5} mb={9}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={8}>
            <MDBox mt={6} mb={8} textAlign="center">
              <MDBox mb={1}>
                <MDTypography variant="h3" fontWeight="bold">
                  Add New User
                </MDTypography>
              </MDBox>
              <MDTypography variant="h5" fontWeight="regular" color="secondary">
                This information will describe more about the user.
              </MDTypography>
            </MDBox>
            <Card>
              <MDBox
                component="form"
                method="POST"
                onSubmit={submitHandler}
                encType="multipart/form-data"
              >
                <MDBox display="flex" flexDirection="column" px={3} my={4}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <FormField
                        label="Name"
                        placeholder="Alec"
                        name="name"
                        value={user.name}
                        onChange={changeHandler}
                        error={error.name}
                        inputProps={{
                          autoComplete: "name",
                          form: {
                            autoComplete: "off",
                          },
                        }}
                      />
                      {error.name && (
                        <MDTypography variant="caption" color="error" fontWeight="light">
                          {error.textError}
                        </MDTypography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormField
                        label="Email"
                        placeholder="example@email.com"
                        inputProps={{
                          type: "email",
                          autoComplete: "email",
                          form: {
                            autoComplete: "off",
                          },
                        }}
                        name="email"
                        value={user.email}
                        onChange={changeHandler}
                        error={error.email}
                      />
                      {error.email && (
                        <MDTypography variant="caption" color="error" fontWeight="light">
                          {error.textError}
                        </MDTypography>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container spacing={3} mt={4}>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        fullWidth
                        label="Current Password"
                        inputProps={{ type: "password", autoComplete: "" }}
                        name="password"
                        value={user.password}
                        onChange={changeHandler}
                        error={error.password}
                      />
                      {error.password && (
                        <MDTypography variant="caption" color="error" fontWeight="light">
                          {error.textError}
                        </MDTypography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        fullWidth
                        label="Confirm Password"
                        inputProps={{ type: "password", autoComplete: "" }}
                        name="confirm"
                        value={user.confirm}
                        onChange={changeHandler}
                        error={error.confirm}
                      />
                      {error.confirm && (
                        <MDTypography variant="caption" color="error" fontWeight="light">
                          {error.textError}
                        </MDTypography>
                      )}
                    </Grid>
                  </Grid>
                  <MDBox display="flex" flexDirection="column" fullWidth>

                    <Grid container spacing={3} mt={4}>

                      <Grid item xs={12} md={4}>
                        <MDBox mt={2} display="flex" flexDirection="column">
                          <MDBox mb={2}>
                            <InputLabel id="demo-simple-select-label">Profile Image</InputLabel>
                          </MDBox>
                          {useMemo(
                            () => (
                              <MDDropzone
                                options={{ addRemoveLinks: true, maxFiles: 1, acceptedFiles: 'image/*' }}
                                onFileAdd={onImageAdd}
                                acceptedFiles="image/*"
                              />
                            ),
                            []
                          )}
                        </MDBox>

                        {imageUrl && (
                          <MDBox ml={4} mt={2}>
                            <MDAvatar src={imageUrl} alt="profile-image" size="xxl" shadow="sm" />
                          </MDBox>
                        )}
                      </Grid>

                      <Grid item xs={12} md={8}>
                        <MDBox
                          display="flex"
                          flexDirection="column"
                          fullWidth
                          marginBottom="1rem"
                          marginTop="3rem"
                        >
                          <MDBox>
                            <Autocomplete
                              defaultValue=""
                              options={roles}
                              getOptionLabel={(option) => (option ? option.attributes.name : "")}
                              value={value ?? ""}
                              onChange={(event, newValue) => {
                                setValue(newValue);
                              }}
                              renderInput={(params) => (
                                <FormField {...params} label="Role" InputLabelProps={{ shrink: true }} />
                              )}
                            />
                            {error.role && (
                              <MDTypography
                                variant="caption"
                                color="error"
                                fontWeight="light"
                                paddingTop="1rem"
                              >
                                {error.textError}
                              </MDTypography>
                            )}
                          </MDBox>
                          {ability.can("create", "organizations") && (
                            <MDBox mt={3}>
                              <Autocomplete
                                defaultValue=""
                                options={organizations}
                                getOptionLabel={(option) => (option ? option.label : "")}
                                value={selOrg ?? ""}
                                onChange={(event, newValue) => {
                                  setSelOrg(newValue);
                                }}
                                renderInput={(params) => (
                                  <FormField {...params} label="organization" InputLabelProps={{ shrink: true }} />
                                )}
                              />
                              {error.organization && (
                                <MDTypography
                                  variant="caption"
                                  color="error"
                                  fontWeight="light"
                                  paddingTop="1rem"
                                >
                                  {error.textError}
                                </MDTypography>
                              )}
                            </MDBox>)}
                        </MDBox>
                      </Grid>
                    </Grid>

                  </MDBox>
                  {error.error && (
                    <MDTypography variant="caption" color="error" fontWeight="light" pt={2}>
                      {error.textError}
                    </MDTypography>
                  )}
                  <MDBox ml="auto" mt={4} mb={2} display="flex" justifyContent="flex-end">
                    <MDBox mx={2}>
                      <MDButton
                        variant="gradient"
                        color="dark"
                        size="small"
                        px={2}
                        mx={2}
                        onClick={() =>
                          navigate("/examples-api/user-management", {
                            state: { value: false, text: "" },
                          })
                        }
                      >
                        Back
                      </MDButton>
                    </MDBox>
                    <MDButton variant="gradient" color="dark" size="small" type="submit">
                      Save
                    </MDButton>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default NewUser;
